import * as i0 from '@angular/core';
import { InjectionToken, isDevMode, Component, ChangeDetectionStrategy, Inject, Optional, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
const _c0 = ["*"];
const _c1 = (a0, a1, a2, a3, a4) => ({
  "custom-content": a0,
  circle: a1,
  progress: a2,
  "progress-dark": a3,
  pulse: a4
});
function NgxSkeletonLoaderComponent_For_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NgxSkeletonLoaderComponent_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 0);
    i0.ɵɵtemplate(1, NgxSkeletonLoaderComponent_For_1_Conditional_1_Template, 1, 0);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction5(5, _c1, ctx_r0.appearance === "custom-content", ctx_r0.appearance === "circle", ctx_r0.animation === "progress", ctx_r0.animation === "progress-dark", ctx_r0.animation === "pulse"))("ngStyle", ctx_r0.theme);
    i0.ɵɵattribute("aria-label", ctx_r0.ariaLabel)("aria-valuetext", ctx_r0.loadingText);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.appearance === "custom-content" ? 1 : -1);
  }
}
const NGX_SKELETON_LOADER_CONFIG = new InjectionToken('ngx-skeleton-loader.config');
let NgxSkeletonLoaderComponent = /*#__PURE__*/(() => {
  class NgxSkeletonLoaderComponent {
    constructor(config) {
      this.config = config;
      const {
        appearance = 'line',
        animation = 'progress',
        theme = null,
        loadingText = 'Loading...',
        count = 1,
        ariaLabel = 'loading'
      } = config || {};
      this.appearance = appearance;
      this.animation = animation;
      this.theme = theme;
      this.loadingText = loadingText;
      this.count = count;
      this.items = [];
      this.ariaLabel = ariaLabel;
    }
    ngOnInit() {
      this.validateInputValues();
    }
    validateInputValues() {
      // Checking if it's receiving a numeric value (string having ONLY numbers or if it's a number)
      if (!/^\d+$/.test(`${this.count}`)) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'count' a numeric value. Forcing default to "1".`);
        }
        this.count = 1;
      }
      //Force count to 1 when custom-content is used
      if (this.appearance === 'custom-content') {
        // Shows error message only in Development
        if (isDevMode() && this.count !== 1) {
          console.error(`\`NgxSkeletonLoaderComponent\` enforces elements with "custom-content" appearance as DOM nodes. Forcing "count" to "1".`);
          this.count = 1;
        }
      }
      this.items.length = this.count;
      const allowedAnimations = ['progress', 'progress-dark', 'pulse', 'false'];
      if (allowedAnimations.indexOf(String(this.animation)) === -1) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'animation' as: ${allowedAnimations.join(', ')}. Forcing default to "progress".`);
        }
        this.animation = 'progress';
      }
      if (['circle', 'line', 'custom-content', ''].indexOf(String(this.appearance)) === -1) {
        // Shows error message only in Development
        if (isDevMode()) {
          console.error(`\`NgxSkeletonLoaderComponent\` need to receive 'appearance' as: circle or line or custom-content or empty string. Forcing default to "''".`);
        }
        this.appearance = '';
      }
      // This code is not using optional chaining since it's only supported on Angular CLI v12
      //  - more details in this comment https://github.com/angular/angular-cli/issues/20471#issuecomment-815599616
      // Which raises issues on the consumer's side. E.G: https://github.com/willmendesneto/ngx-skeleton-loader/issues/150#issuecomment-1638612329
      const {
        theme
      } = this.config || {};
      if (!!theme && !!theme.extendsFromRoot && this.theme !== null) {
        // Shows error message only in Development
        this.theme = {
          ...this.config.theme,
          ...this.theme
        };
      }
    }
    ngOnChanges(changes) {
      // Avoiding multiple calls for the same input in case there's no changes in the fields
      // Checking if the fields that require validation are available and if they were changed
      // In case were not changed, we stop the function. Otherwise, `validateInputValues` will be called.
      if (['count', 'animation', 'appearance'].find(key => changes[key] && (changes[key].isFirstChange() || changes[key].previousValue === changes[key].currentValue))) {
        return;
      }
      this.validateInputValues();
    }
    /** @nocollapse */
    static {
      this.ɵfac = function NgxSkeletonLoaderComponent_Factory(t) {
        return new (t || NgxSkeletonLoaderComponent)(i0.ɵɵdirectiveInject(NGX_SKELETON_LOADER_CONFIG, 8));
      };
    }
    /** @nocollapse */
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgxSkeletonLoaderComponent,
        selectors: [["ngx-skeleton-loader"]],
        inputs: {
          count: "count",
          loadingText: "loadingText",
          appearance: "appearance",
          animation: "animation",
          ariaLabel: "ariaLabel",
          theme: "theme"
        },
        features: [i0.ɵɵNgOnChangesFeature],
        ngContentSelectors: _c0,
        decls: 2,
        vars: 0,
        consts: [["aria-busy", "true", "aria-valuemin", "0", "aria-valuemax", "100", "role", "progressbar", "tabindex", "-1", 1, "skeleton-loader", 3, "ngClass", "ngStyle"]],
        template: function NgxSkeletonLoaderComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵrepeaterCreate(0, NgxSkeletonLoaderComponent_For_1_Template, 2, 11, "div", 0, i0.ɵɵrepeaterTrackByIdentity);
          }
          if (rf & 2) {
            i0.ɵɵrepeater(ctx.items);
          }
        },
        dependencies: [i1.NgClass, i1.NgStyle],
        styles: [".skeleton-loader[_ngcontent-%COMP%]{box-sizing:border-box;overflow:hidden;position:relative;background:#eff1f6 no-repeat;border-radius:4px;width:100%;height:20px;display:inline-block;margin-bottom:10px;will-change:transform}.skeleton-loader[_ngcontent-%COMP%]:after, .skeleton-loader[_ngcontent-%COMP%]:before{box-sizing:border-box}.skeleton-loader.circle[_ngcontent-%COMP%]{width:40px;height:40px;margin:5px;border-radius:50%}.skeleton-loader.progress[_ngcontent-%COMP%], .skeleton-loader.progress-dark[_ngcontent-%COMP%]{transform:translateZ(0)}.skeleton-loader.progress[_ngcontent-%COMP%]:after, .skeleton-loader.progress[_ngcontent-%COMP%]:before, .skeleton-loader.progress-dark[_ngcontent-%COMP%]:after, .skeleton-loader.progress-dark[_ngcontent-%COMP%]:before{box-sizing:border-box}.skeleton-loader.progress[_ngcontent-%COMP%]:before, .skeleton-loader.progress-dark[_ngcontent-%COMP%]:before{animation:_ngcontent-%COMP%_progress 2s ease-in-out infinite;background-size:200px 100%;position:absolute;z-index:1;top:0;left:0;width:200px;height:100%;content:\"\"}.skeleton-loader.progress[_ngcontent-%COMP%]:before{background-image:linear-gradient(90deg,#fff0,#fff9,#fff0)}.skeleton-loader.progress-dark[_ngcontent-%COMP%]:before{background-image:linear-gradient(90deg,transparent,rgba(0,0,0,.2),transparent)}.skeleton-loader.pulse[_ngcontent-%COMP%]{animation:_ngcontent-%COMP%_pulse 1.5s cubic-bezier(.4,0,.2,1) infinite;animation-delay:.5s}.skeleton-loader.custom-content[_ngcontent-%COMP%]{height:100%;background:none}@media (prefers-reduced-motion: reduce){.skeleton-loader.pulse[_ngcontent-%COMP%], .skeleton-loader.progress-dark[_ngcontent-%COMP%], .skeleton-loader.custom-content[_ngcontent-%COMP%], .skeleton-loader.progress[_ngcontent-%COMP%]:before{animation:none}.skeleton-loader.progress[_ngcontent-%COMP%]:before, .skeleton-loader.progress-dark[_ngcontent-%COMP%], .skeleton-loader.custom-content[_ngcontent-%COMP%]{background-image:none}}@media screen and (min-device-width: 1200px){.skeleton-loader[_ngcontent-%COMP%]{-webkit-user-select:none;user-select:none;cursor:wait}}@keyframes _ngcontent-%COMP%_progress{0%{transform:translate3d(-200px,0,0)}to{transform:translate3d(calc(200px + 100vw),0,0)}}@keyframes _ngcontent-%COMP%_pulse{0%{opacity:1}50%{opacity:.4}to{opacity:1}}"],
        changeDetection: 0
      });
    }
  }
  return NgxSkeletonLoaderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxSkeletonLoaderModule = /*#__PURE__*/(() => {
  class NgxSkeletonLoaderModule {
    static forRoot(config) {
      return {
        ngModule: NgxSkeletonLoaderModule,
        providers: [{
          provide: NGX_SKELETON_LOADER_CONFIG,
          useValue: config
        }]
      };
    }
    /** @nocollapse */
    static {
      this.ɵfac = function NgxSkeletonLoaderModule_Factory(t) {
        return new (t || NgxSkeletonLoaderModule)();
      };
    }
    /** @nocollapse */
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxSkeletonLoaderModule
      });
    }
    /** @nocollapse */
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return NgxSkeletonLoaderModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-skeleton-loader
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NGX_SKELETON_LOADER_CONFIG, NgxSkeletonLoaderComponent, NgxSkeletonLoaderModule };
